import { useCallback, useEffect, useState } from "react";
import { ApiError, type ForecastRow, OBSTACLES, type Oracle, api, label } from "../api.js";

/**
 * Кадр «Оракул» (CONCEPT.md, решение 10). Предсказатель и судья в одном экране.
 *
 * ⚠️ **Ни одного числа этот экран не производит.** Всё, что здесь показано, посчитано
 * кодом по леджеру или прочитано из локального корпуса; модель к экрану не подключена
 * вовсе, и он полностью работает без ANTHROPIC_API_KEY.
 *
 * ⚠️ **Своей вероятности агент не называет, пока разрешённых прогнозов меньше порога.**
 * До порога личного референсного класса не существует, а литературный приор числом
 * не показывается никогда: якорь работает и с дисклеймером. Поэтому колонки агента
 * до права нет вовсе — пустая колонка была бы приглашением её заполнить.
 *
 * Разрешение прогнозов живёт здесь же, и это не украшение: до этого экрана замкнуть
 * петлю калибровки в интерфейсе было нечем — ручка `/api/forecasts/:id/resolve`
 * существовала, но не вызывалась ниоткуда, а значит право на вероятность не могло
 * наступить никогда.
 */

export function OracleScreen(): React.JSX.Element {
  const [data, setData] = useState<Oracle | null>(null);
  const [goalId, setGoalId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const query = goalId ? `?goal_id=${encodeURIComponent(goalId)}` : "";
    setData(await api.get<Oracle>(`/api/oracle${query}`));
  }, [goalId]);

  useEffect(() => {
    void reload().catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : "оракул не отвечает")
    );
  }, [reload]);

  if (error) return <p className="empty">{error}</p>;
  if (!data) return <p className="empty">считаю…</p>;

  return (
    <>
      <header className="head">
        <h1>Оракул</h1>
      </header>
      <p className="subhead">Предсказатель и судья. Числа считает код, а не модель.</p>

      <Gate oracle={data} />

      {data.premortem && (
        <Premortem
          oracle={data}
          onPick={setGoalId}
          onChanged={() => void reload()}
          onError={setError}
        />
      )}

      <Forecasts oracle={data} onChanged={() => void reload()} onError={setError} />
      <Judge oracle={data} />
    </>
  );
}

/** Право на вероятность. Момент его получения обязан быть виден явно — решение 10. */
function Gate({ oracle }: { oracle: Oracle }): React.JSX.Element {
  const { gate, calibration } = oracle;
  const cells = Array.from({ length: gate.threshold }, (_, index) => index < gate.resolved_n);

  return (
    <section className="card gate">
      <div className="gate-title">
        {gate.allowed ? "Право на вероятность получено" : "Своей вероятности не называю"}
      </div>
      <p className="gate-why">{gate.reason}</p>

      <div className="cells">
        {cells.map((on, index) => (
          <i key={index} className={on ? "on" : ""} />
        ))}
      </div>
      <div className="gate-line">
        <span>
          {gate.allowed ? (
            <>личный референсный класс есть</>
          ) : (
            <>
              до права на вероятность: ещё <b className="num">{gate.remaining}</b>
            </>
          )}
        </span>
        <span className="of num">
          {gate.resolved_n} / {gate.threshold}
        </span>
      </div>

      {gate.allowed && calibration.agent && (
        <div className="gate-cal">
          <span>
            твой Brier <b className="num">{calibration.owner.binary.brier ?? "—"}</b>
          </span>
          <span>
            агента <b className="num">{calibration.agent.binary.brier ?? "—"}</b>
          </span>
          <span className="of num">n = {calibration.owner.binary.n}</span>
        </div>
      )}

      <p className="gate-note">
        Литературный приор — «привычки закрепляются у стольких-то процентов» — числом
        не покажу никогда. Это выборка чужих людей, выданная за твою базовую ставку.
      </p>
    </section>
  );
}

/**
 * Премортем. Причины пишет владелец; подсказки приносит КОД из локального корпуса
 * по осям цели — поэтому каждая подсказка может назвать карточку, из которой пришла.
 */
function Premortem({
  oracle,
  onPick,
  onChanged,
  onError
}: {
  oracle: Oracle;
  onPick: (id: string) => void;
  onChanged: () => void;
  onError: (message: string) => void;
}): React.JSX.Element | null {
  const block = oracle.premortem;
  const [text, setText] = useState("");
  const [obstacle, setObstacle] = useState("time");
  // Подсказка корпуса выбирает КОД препятствия, причину пишет владелец. Записывать
  // за него «помешало: внимание» бессмысленно: ценность премортема в его собственных
  // словах, а на разборе такая строка ничего не скажет. Slug держится до отправки,
  // чтобы происхождение подсказки не потерялось.
  const [fromCorpus, setFromCorpus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!block) return null;

  async function add() {
    setBusy(true);
    try {
      await api.post(`/api/goals/${block!.goal.id}/premortem`, {
        text,
        obstacle_code: obstacle,
        ...(fromCorpus ? { method_slug: fromCorpus } : {})
      });
      setText("");
      setFromCorpus(null);
      onChanged();
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : "не записалось");
    } finally {
      setBusy(false);
    }
  }

  async function drop(id: string) {
    try {
      await api.del(`/api/premortem/${id}`);
      onChanged();
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : "не удалилось");
    }
  }

  return (
    <>
      <div className="sec">Премортем · «{block.goal.title}»</div>
      <section className="card premortem">
        {oracle.premortem_goals.length > 1 && (
          <div className="chips picker">
            {oracle.premortem_goals.map((goal) => (
              <button
                key={goal.id}
                className={`chip ${goal.id === block.goal.id ? "on" : ""}`}
                onClick={() => onPick(goal.id)}
              >
                {goal.title}
              </button>
            ))}
          </div>
        )}

        <div className="pm-ask">Представь, что провалил. Почему?</div>

        {block.items.length === 0 && (
          <p className="pm-empty">
            Ни одной причины не названо. Смысл приёма в количестве, а не в качестве:
            причина, названная до провала, потом сверяется с тем, что случилось на самом деле.
          </p>
        )}

        {block.items.map((item) => (
          <div className="pm-item" key={item.id}>
            <span className="pm-text">
              {item.text}
              <span className="pm-meta">
                {label(item.obstacle_code)}
                {item.method_slug ? ` · подсказка корпуса: ${item.method_slug}` : ""}
              </span>
            </span>
            <button className="pm-drop" aria-label="убрать причину" onClick={() => void drop(item.id)}>
              ×
            </button>
          </div>
        ))}

        <div className="pm-form">
          <input
            className="field"
            value={text}
            placeholder="Третий слот в четверг вечером не выживает"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && text.trim().length >= 3) void add();
            }}
          />
          <select
            className="field"
            value={obstacle}
            onChange={(event) => {
              setObstacle(event.target.value);
              // Владелец сменил код руками — подсказка корпуса больше ни при чём.
              setFromCorpus(null);
            }}
            aria-label="код препятствия"
          >
            {OBSTACLES.filter(([code]) => code !== "none").map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
          <button className="btn primary" disabled={busy || text.trim().length < 3} onClick={() => void add()}>
            Добавить
          </button>
        </div>

        {block.typical.length > 0 && (
          <div className="pm-typical">
            <div className="pm-typical-head">Типичные для таких целей — из корпуса</div>
            <div className="chips">
              {block.typical.slice(0, 5).map((hint) => (
                <button
                  key={hint.obstacle_code}
                  className={`chip more ${obstacle === hint.obstacle_code && fromCorpus ? "on" : ""}`}
                  onClick={() => {
                    setObstacle(hint.obstacle_code);
                    setFromCorpus(hint.methods[0]?.slug ?? null);
                  }}
                >
                  {label(hint.obstacle_code)}
                </button>
              ))}
            </div>
            <p className="pm-note">
              {fromCorpus
                ? `Код препятствия взят из подсказки «${fromCorpus}» — напиши, как именно это
                   тебя остановит. Причина остаётся твоей, помечена будет подсказка.`
                : `Подсказки принёс код по осям цели из карточек корпуса, а не модель. Они выбирают
                   код препятствия — причину пишешь ты. Качественно: числа тут не будет,
                   премортем даёт причины, а не вероятности.`}
            </p>
          </div>
        )}

        <div className="pm-check">
          {block.checkable.machine ? (
            <span className="judge">критерий численный → судить будет код</span>
          ) : (
            <span className="pm-warn">
              Критерий не машинно-проверяемый: судить придётся тебе или модели. До старта это
              ещё можно дожать — после запуска правка станет ревизией.
            </span>
          )}
        </div>
      </section>
    </>
  );
}

/**
 * Прогнозы — открытые и разрешённые в ОДНОЙ панели с внутренними подписями групп.
 *
 * В эталоне это две отдельные панели. Здесь они слиты по той же причине, по которой
 * слиты субнавыки на «Навыке»: с премортемом и лентой судьи на экране одновременно
 * оказывалось пять стеклянных слоёв при лимите в четыре.
 *
 * Разрешение живёт здесь же, и это не украшение: до этого экрана замкнуть петлю
 * калибровки в интерфейсе было нечем, а без разрешённых прогнозов право на вероятность
 * не наступит никогда.
 */
function Forecasts({
  oracle,
  onChanged,
  onError
}: {
  oracle: Oracle;
  onChanged: () => void;
  onError: (message: string) => void;
}): React.JSX.Element {
  const today = new Date().toISOString().slice(0, 10);

  const [asking, setAsking] = useState<string | null>(null);

  async function resolve(id: string, resolution: "yes" | "no" | "ambiguous") {
    try {
      await api.post(`/api/forecasts/${id}/resolve`, { resolution });
      onChanged();
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : "не разрешилось");
    }
  }

  /**
   * Спросить агента о ТОМ ЖЕ прогнозе, что сделал владелец.
   *
   * Смысл именно в совпадении вопроса и срока: две калибровки рядом сравнимы только
   * тогда, когда предсказывали одно и то же. Право на это агент получает не раньше
   * порога разрешённых прогнозов — до него ворота закрыты и кнопки нет вовсе.
   */
  async function askAgent(forecast: ForecastRow) {
    setAsking(forecast.id);
    try {
      await api.post(`/api/goals/${forecast.subject_id}/agent-forecast`, {
        question: forecast.question,
        resolve_by: forecast.resolve_by,
        kind: forecast.kind === "date" ? "date" : "binary"
      });
      onChanged();
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : "агент не ответил");
    } finally {
      setAsking(null);
    }
  }

  /** Уже спрошенное не спрашиваем дважды: у агента на этот вопрос прогноз есть. */
  const agentAnswered = new Set(
    [...oracle.open_forecasts, ...oracle.resolved_forecasts]
      .filter((forecast) => forecast.forecaster === "agent")
      .map((forecast) => `${forecast.subject_id}|${forecast.question}`)
  );

  const { resolved_forecasts: resolved, calibration, gate } = oracle;
  if (oracle.open_forecasts.length === 0 && resolved.length === 0) {
    return (
      <>
        <div className="sec">Прогнозы</div>
        <p className="empty">Прогнозов пока нет. Свой прогноз записывается при заведении цели.</p>
      </>
    );
  }

  return (
    <>
      <div className="sec">Прогнозы</div>
      <section className="rows">
        {oracle.open_forecasts.length > 0 && (
          <>
            <div className="rows-group">Открытые</div>
            {oracle.open_forecasts.map((forecast) => {
              const due = forecast.resolve_by <= today;
              return (
                <div className="row open fc" key={forecast.id}>
                  <div className="fc-top">
                    <span className="two-line">
                      {forecast.subject_title ?? forecast.question}
                      <span className="hint">
                        разрешится <span className="num">{forecast.resolve_by}</span>
                        {forecast.forecaster === "agent" ? " · прогноз агента" : " · твой"}
                      </span>
                    </span>
                    <span className="fc-p num">
                      {forecast.prob === null ? forecast.date_p50 : `${Math.round(forecast.prob * 100)}%`}
                    </span>
                  </div>
                  {gate.allowed &&
                    forecast.forecaster === "owner" &&
                    !agentAnswered.has(`${forecast.subject_id}|${forecast.question}`) && (
                      <div className="choices fc-act">
                        <button
                          className="btn ghost"
                          disabled={asking === forecast.id}
                          onClick={() => void askAgent(forecast)}
                        >
                          {asking === forecast.id ? "агент считает…" : "Спросить агента"}
                        </button>
                      </div>
                    )}
                  {due && (
                    <div className="choices fc-act">
                      <button className="btn primary" onClick={() => void resolve(forecast.id, "yes")}>
                        Сбылось
                      </button>
                      <button className="btn" onClick={() => void resolve(forecast.id, "no")}>
                        Не сбылось
                      </button>
                      <button className="btn ghost" onClick={() => void resolve(forecast.id, "ambiguous")}>
                        Не разобрать
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {resolved.length > 0 && (
          <>
            <div className="rows-group">Разрешённые</div>
            {resolved.map((forecast) => (
              <div className="row" key={forecast.id}>
                <span className="two-line">
                  {forecast.subject_title ?? forecast.question}
                  <span className="hint">
                    разрешён <span className="num">{forecast.resolved_at?.slice(0, 10)}</span> ·{" "}
                    {forecast.resolution === "yes" ? (
                      "сбылось"
                    ) : forecast.resolution === "no" ? (
                      <span className="miss-mark">не сбылось</span>
                    ) : (
                      label(forecast.resolution ?? "")
                    )}
                  </span>
                </span>
                <span className="sub num">
                  {forecast.prob === null ? "—" : `${Math.round(forecast.prob * 100)}%`}
                </span>
              </div>
            ))}
            <div className="row">
              <span className="two-line">
                Brier и смещение
                <span className="hint">
                  {gate.allowed
                    ? `Brier ${calibration.owner.binary.brier ?? "—"} · смещение ${
                        calibration.owner.binary.overconfidence ?? "—"
                      } п.п.`
                    : "порог не набран — считать не на чем"}
                </span>
              </span>
              <span className="sub num">n = {calibration.owner.binary.n}</span>
            </div>
          </>
        )}
      </section>
      {!gate.allowed && (
        <p className="note-under">
          Колонка агента появится, когда наберётся право. Пустой она не показывается — это было бы
          приглашением её заполнить.
        </p>
      )}
    </>
  );
}

/** Судья. Пункты складывает код, а не решение модели. */
function Judge({ oracle }: { oracle: Oracle }): React.JSX.Element | null {
  if (oracle.verdicts.length === 0) return null;
  return (
    <>
      <div className="sec">Судья</div>
      <section className="rows">
        {oracle.verdicts.map((verdict) => {
          const decision = verdict.final_decision ?? verdict.decision;
          return (
            <div className="row" key={verdict.id}>
              <span className="two-line">
                {verdict.goal_title ?? "цель удалена"}
                <span className="hint">
                  судил {label(verdict.judged_by)}
                  {verdict.missing ? ` · не закрыт пункт: ${verdict.missing}` : ""}
                </span>
              </span>
              <span className={`sub ${decision === "accepted" ? "" : "miss-mark"}`}>
                {decision === "accepted" ? "засчитано" : "не засчитано"}
              </span>
            </div>
          );
        })}
      </section>
      <p className="note-under">
        Судья складывает пункты правила кодом, а не решением модели. Апелляция требует письменной
        причины, и доля переопределений сама по себе метрика.
      </p>
    </>
  );
}
